import axios from 'axios';
import { API_BASE_URL } from '../components/config/api';
import { addClientBuildHeader } from '../utils/clientBuild';
import type { Lead, LeadActivity, LeadInput, SalesMeta } from '../components/sales/salesModel';

export interface SalesReportRow { key: string; total: number; won: number; lost: number; active: number; won_value: number }
export interface SalesReport {
  days: number | null;
  stages: Array<{ status: string; n: number; valued: number; value: number }>;
  summary: {
    new_leads: number; won: number; lost: number; won_value: number; median_days_to_win: number | null; contacts: number;
  };
  by_source: SalesReportRow[];
  by_kind: SalesReportRow[];
  lost_reasons: Array<{ reason: string; n: number }>;
  lost_notes: Array<{ id: number; name: string; reason: string; note: string; occurred_at: string }>;
  weekly: Array<{ week: string; calls: number; meetings: number; other: number }>;
}

// API των πωλήσεων (/api/sales, migration 285). Ίδιο token/interceptors με το supportApi.
const salesApi = axios.create({
  baseURL: `${API_BASE_URL}/api/sales`,
  headers: { 'Content-Type': 'application/json' },
});

salesApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('xentrix_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return addClientBuildHeader(config);
  },
  (error) => Promise.reject(error)
);

salesApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('xentrix_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface ActivityInput {
  type: string;
  outcome?: string | null;
  body?: string | null;
}

export const salesAPI = {
  // Διακόπτης: τον αλλάζει μόνο ο super admin
  getStatus: () => salesApi.get('/status') as Promise<{ enabled: boolean; is_super: boolean }>,
  setStatus: (enabled: boolean) => salesApi.put('/status', { enabled }) as Promise<{ enabled: boolean; is_super: boolean }>,

  getMeta: () => salesApi.get('/meta') as Promise<SalesMeta>,
  getLeads: () => salesApi.get('/leads') as Promise<{ leads: Lead[] }>,
  getLead: (id: number) => salesApi.get(`/leads/${id}`) as Promise<{ lead: Lead; activities: LeadActivity[] }>,
  createLead: (data: LeadInput) => salesApi.post('/leads', data) as Promise<{ lead: Lead }>,
  updateLead: (id: number, data: LeadInput) => salesApi.put(`/leads/${id}`, data) as Promise<{ lead: Lead }>,
  // Επαφή (κλήση, επίσκεψη, σημείωση…) και, μαζί, στάδιο / επόμενο βήμα — μία συναλλαγή.
  addActivity: (id: number, activity: ActivityInput, lead: LeadInput = {}) =>
    salesApi.post(`/leads/${id}/activities`, { activity, lead }) as Promise<{ lead: Lead }>,
  deleteLead: (id: number) => salesApi.delete(`/leads/${id}`) as Promise<{ deleted: number }>,
  // Αναφορές: περίοδος 30/90/365 ημερών ή null = όλα.
  getReports: (days: number | null) => salesApi.get('/reports', { params: days ? { days } : {} }) as Promise<SalesReport>,
  // Χάρτης (migration 286): σημείο που βρήκε ο browser, ή { precision: 'none' } όταν δεν βρέθηκε.
  setGeo: (id: number, geo: { lat?: number; lng?: number; precision: 'address' | 'area' | 'manual' | 'none' }) =>
    salesApi.put(`/leads/${id}/geo`, geo) as Promise<{ geo: { id: number; lat: number | null; lng: number | null; geo_precision: string } }>,
};
